export type LogLevel = "debug" | "info" | "warn" | "error";

const LEVEL_ORDER: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };

const REDACT_KEYS = new Set(["password", "secret", "token", "raw", "authorization", "cookie", "apikey", "api_key"]);

const redact = (v: unknown): unknown => {
  if (v === null || v === undefined) return v;
  if (typeof v !== "object") return v;
  if (v instanceof Error) {
    const err = v as Error & {
      statusCode?: unknown;
      responseBody?: unknown;
      data?: unknown;
      cause?: unknown;
    };
    return {
      name: err.name,
      message: err.message,
      stack: err.stack,
      ...(err.statusCode !== undefined ? { statusCode: err.statusCode } : {}),
      ...(err.responseBody !== undefined ? { responseBody: redact(err.responseBody) } : {}),
      ...(err.data !== undefined ? { data: redact(err.data) } : {}),
      ...(err.cause !== undefined ? { cause: redact(err.cause) } : {}),
    };
  }
  if (Array.isArray(v)) return v.map(redact);
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    out[k] = REDACT_KEYS.has(k.toLowerCase()) ? "[REDACTED]" : redact(val);
  }
  return out;
};

export type LogFields = Record<string, unknown>;

export type Sink = (level: LogLevel, line: Record<string, unknown>) => void;

const defaultSink: Sink = (level, line) => {
  const json = JSON.stringify(line);
  if (level === "error") console.error(json);
  else if (level === "warn") console.warn(json);
  else if (level === "debug") console.debug(json);
  else console.log(json);
};

let currentSink: Sink = defaultSink;
let currentMinLevel: LogLevel = "info";

export const configureLogger = (opts: { minLevel?: LogLevel; sink?: Sink }): void => {
  if (opts.minLevel) currentMinLevel = opts.minLevel;
  if (opts.sink) currentSink = opts.sink;
};

const shouldLog = (level: LogLevel): boolean => LEVEL_ORDER[level] >= LEVEL_ORDER[currentMinLevel];

export type Logger = {
  debug: (event: string, fields?: LogFields) => void;
  info: (event: string, fields?: LogFields) => void;
  warn: (event: string, fields?: LogFields) => void;
  error: (event: string, fields?: LogFields) => void;
  child: (bindings: LogFields) => Logger;
};

const build = (component: string, bindings: LogFields): Logger => {
  const emit = (level: LogLevel, event: string, fields: LogFields = {}) => {
    if (!shouldLog(level)) return;
    const line = {
      ts: new Date().toISOString(),
      level,
      component,
      event,
      ...(redact(bindings) as LogFields),
      ...(redact(fields) as LogFields),
    };
    currentSink(level, line);
  };
  return {
    debug: (event, fields) => { emit("debug", event, fields); },
    info: (event, fields) => { emit("info", event, fields); },
    warn: (event, fields) => { emit("warn", event, fields); },
    error: (event, fields) => { emit("error", event, fields); },
    child: (extra) => build(component, { ...bindings, ...extra }),
  };
};

export const createLogger = (component: string, bindings: LogFields = {}): Logger => build(component, bindings);

export const newRequestId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};
